import React, { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import SearchFilter from "../Common/SearchFilter";
import TenantCard from "./TenantCard";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: "100vh",
    background: "#F4F5F7",
    padding: "2rem 1.75rem",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  pageTitle: { fontSize: 20, fontWeight: 600, color: "#1a1d23", marginBottom: 3 },
  pageSub: { fontSize: 13, color: "#6b7280" },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHead: {
    padding: "0.75rem 1rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fafafa",
  },
  addBtn: {
    fontSize: 13,
    fontWeight: 600,
    padding: "7px 18px",
    borderRadius: 8,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  clearBtn: {
    fontSize: 12,
    color: "#6b7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  spinner: {
    display: "inline-block",
    width: 18,
    height: 18,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 8,
  },
  errorBanner: {
    fontSize: 13,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: "1rem",
  },
  retryBtn: {
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 14px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    marginTop: 8,
  },
};

const EMPTY_FILTERS = { status: "", paymentStatus: "", leaseStatus: "", minRent: "", maxRent: "" };

const FILTER_CONFIG = {
  searchPlaceholder: "Search by name, email, phone…",
  title: "Search & filters",
  filterFields: [
    {
      name: "status",
      type: "select",
      placeholder: "All statuses",
      options: [
        { value: "active", label: "Active" },
        { value: "prospective", label: "Prospective" },
        { value: "past", label: "Past" },
        { value: "evicted", label: "Evicted" },
      ],
    },
    {
      name: "paymentStatus",
      type: "select",
      placeholder: "Payment status",
      options: [
        { value: "paid", label: "Paid" },
        { value: "late", label: "Late" },
        { value: "overdue", label: "Overdue" },
        { value: "partial", label: "Partial" },
      ],
    },
    {
      name: "leaseStatus",
      type: "select",
      placeholder: "Lease status",
      options: [
        { value: "Active", label: "Active" },
        { value: "Expiring Soon", label: "Expiring Soon" },
        { value: "Expired", label: "Expired" },
        { value: "No active lease", label: "No active lease" },
      ],
    },
    { name: "minRent", type: "number", placeholder: "Min rent" },
    { name: "maxRent", type: "number", placeholder: "Max rent" },
  ],
};

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const FilterIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="11" y1="18" x2="13" y2="18" />
  </svg>
);

// ── Component ──────────────────────────────────────────────────────────────────
function TenantList({ onAddTenant, onViewDetails }) {
  const { getAuthHeaders } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let filtered = tenants;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.personalInfo.firstName?.toLowerCase().includes(q) ||
          t.personalInfo.lastName?.toLowerCase().includes(q) ||
          t.personalInfo.email?.toLowerCase().includes(q) ||
          t.personalInfo.phone?.includes(searchTerm)
      );
    }
    if (filters.status) filtered = filtered.filter((t) => t.status === filters.status);
    if (filters.paymentStatus) filtered = filtered.filter((t) => t.currentPaymentStatus === filters.paymentStatus);
    if (filters.leaseStatus) filtered = filtered.filter((t) => t.currentLeaseStatus === filters.leaseStatus);
    if (filters.minRent || filters.maxRent) {
      filtered = filtered.filter((t) => {
        const rent = t.activeLease?.monthlyRent;
        if (!rent) return false;
        const min = filters.minRent ? parseInt(filters.minRent) : 0;
        const max = filters.maxRent ? parseInt(filters.maxRent) : Infinity;
        return rent >= min && rent <= max;
      });
    }
    setFilteredTenants(filtered);
  }, [tenants, searchTerm, filters]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(API_ENDPOINTS.tenants.getAll, {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      if (!response.ok) throw new Error(`Error: ${response.status} - ${response.statusText}`);
      const data = await response.json();
      setTenants(data.data || []);
    } catch (err) {
      console.error("Error fetching tenants:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearchTerm(""); };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: "#6b7280", fontSize: 14 }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <span style={s.spinner} />
        Loading tenants…
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={s.errorBanner}>
          <strong>Error loading tenants</strong>
          <p style={{ margin: "4px 0 0" }}>{error}</p>
          <button style={s.retryBtn} onClick={fetchTenants}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page} className="re-detail">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.5rem", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={s.pageTitle}>Tenants</div>
          <div style={s.pageSub}>Manage your tenant relationships</div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <button style={s.addBtn} onClick={onAddTenant}>
            <PlusIcon /> Add tenant
          </button>
        </div>
      </div>

      {/* ── Search & Filters ── */}
      <div style={{ ...s.card, marginBottom: "1.25rem" }}>
        <div style={s.cardHead}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Search &amp; filters</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {showFilters && (
              <button style={s.clearBtn} onClick={clearFilters}>Clear all</button>
            )}
            <button
              style={{
                fontSize: 12,
                fontWeight: 500,
                padding: "4px 12px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: showFilters ? "#1E40AF" : "#fff",
                color: showFilters ? "#fff" : "#374151",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
              onClick={() => setShowFilters((v) => !v)}
            >
              <FilterIcon />
              {showFilters ? "Hide filters" : "Show filters"}
            </button>
          </div>
        </div>
        {showFilters && (
          <div style={{ padding: "0.875rem 1rem" }}>
            <SearchFilter
              searchTerm={searchTerm}
              filters={filters}
              onSearchChange={setSearchTerm}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
              config={FILTER_CONFIG}
            />
          </div>
        )}
      </div>

      {/* ── Results bar ── */}
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: "1rem" }}>
        <strong style={{ color: "#1a1d23" }}>{filteredTenants.length}</strong>{" "}
        {filteredTenants.length === 1 ? "tenant" : "tenants"} found
      </div>

      {/* ── Grid / Empty state ── */}
      {filteredTenants.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "3rem", textAlign: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No tenants found</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {tenants.length === 0
              ? "You haven't added any tenants yet."
              : "Try adjusting your search or filters."}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {filteredTenants.map((tenant) => (
            <TenantCard key={tenant._id} tenant={tenant} onViewDetails={onViewDetails} />
          ))}
        </div>
      )}
    </div>
  );
}

export default TenantList;
